import apiClient from "@/api/apiClient";
import { useSession } from "@/stores/session";
import { removeCookie } from "@/utils/cookieUtil";
import type { Credentials, User } from "@/types";

class AuthAPI {
  private readonly model: string;
  constructor() {
    this.model = "auth"; // Base path for authentication API
  }

  /**
   * Logs in the user by sending credentials to the server.
   * Stores the access token and admin status locally.
   *
   * @param {Credentials} credentials - The user's login credentials (username, password).
   * @returns {Promise<void>}
   */
  async login(
    credentials: Credentials
  ): Promise<{ isAdmin: boolean; user: User }> {
    const { authorize, initUser } = useSession();
    const response = await apiClient.post<{ isAdmin: boolean; user: User }>(
      `${this.model}/login`,
      credentials
    );
    authorize();
    initUser();
    return response.data;
  }

  /**
   * Logs out the user by clearing session tokens and calling the server to invalidate the refresh token.
   * @returns {Promise<void>}
   */
  async logout(): Promise<void> {
    removeCookie("exposedId");
    sessionStorage.removeItem("loginReloaded");
    return await apiClient.post(`${this.model}/logout`).then(() => {
      const { unAuthorize } = useSession();
      unAuthorize();
    });
  }

  /**
   * Refresh the authentication token.
   * Updates the session on success or logs out on failure.
   * @returns {Promise<void>}
   */
  async refresh(): Promise<void> {
    const { authorize, unAuthorize } = useSession();
    try {
      await apiClient.post(`${this.model}/refresh`);
      authorize();
    } catch (error) {
      unAuthorize();
    }
  }
}

export default new AuthAPI();
